/**
 * A project's risks and issues: what may go wrong and what has, each with a rating in one word,
 * who looks after it and what is being done. Raised and changed here or from a workbench; every
 * change is signed by the person it was for and lands in the project's activity log.
 */

import { BUG_SEVERITIES, byUrgency, hasManyTeams, isOpenEntry, RISK_LEVELS, RISK_RATING_TEXT, RISK_STATUS_TEXT, statusesFor, type BugSeverity, riskRating, type RiskDraft, type RiskEntry, type RiskKind, type RiskLevel, type RiskPatch, type RiskStatus } from '@aioe/shared';
import { dateShort } from '@aioe/shared/wording/dateTime';
import { TriangleExclamation } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { useAgentName } from '../agentNames.ts';
import { timeAgo, useLoad, useNow } from '../hooks.ts';
import { inFocus, RiskOverview, type RiskFocus } from './risks/RiskOverview.tsx';
import { useRootFilter } from './RootFilter.tsx';
import { OwnerPicker } from './OwnerPicker.tsx';
import { SubjectName } from './SubjectPicker.tsx';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, Stat, StatusMark } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';
import { FormField, FormSheet } from './ui/FormSheet.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { Markdown } from './Markdown.tsx';

/** A ListBox id for the empty answer (none, any, all): an empty string is not a reliable id. */
const NONE = '__none';

const LEVEL_TEXT: Record<RiskLevel, string> = { low: 'Low', medium: 'Medium', high: 'High' };
const SEVERITY_TEXT: Record<BugSeverity, string> = { blocker: 'Blocker', critical: 'Critical', major: 'Major', minor: 'Minor', trivial: 'Trivial' };
const RATING_STATE = { critical: 'error', high: 'high', medium: 'medium', low: 'low' } as const;

export function ProjectRisks({ projectId, canWrite, roots }: { projectId: string; canWrite: boolean; roots: string[] }) {
  const tone = VIEW_TONES.projects!;
  const [state, setState] = useState<'open' | 'closed' | 'all'>('open');
  // Everything once; the view and the numbers are cut from it here.
  const data = useLoad(() => routes.projectRisks(projectId, { state: 'all' }), [projectId], 30_000);
  // The template, appetite and review cadence the heat map and its numbers go by (none saved: the built-in 5×5).
  const settings = useLoad(() => routes.projectRiskSettings(projectId).catch(() => undefined), [projectId], 5 * 60_000);
  const now = useNow();
  /** A cell, band or step of the overview the list is narrowed to. */
  const [focus, setFocus] = useState<RiskFocus>();
  const [raising, setRaising] = useState<RiskKind>();
  const [removing, setRemoving] = useState<{ id: string; uid: string }>();
  /** The entry whose owner is being chosen. */
  const [choosing, setChoosing] = useState<string>();
  const [error, setError] = useState<string>();
  const filter = useRootFilter(projectId);
  const all = (data.data?.entries ?? []).filter((e) => filter.root === '__all' || e.rootId === filter.root);
  // A focus from the overview names its own entries, open or settled.
  const entries = all.filter((e) => (focus ? inFocus(e, focus) : state === 'all' || isOpenEntry(e) === (state === 'open'))).sort(byUrgency);
  // A team is named only when more than one has raised entries (D239).
  const manyTeams = hasManyTeams(all.map((e) => e.raisedByTeam?.id));
  const people = data.data?.people ?? {};
  const agentName = useAgentName();
  const who = (p?: { principal?: string; actor?: string }) => (p?.principal ? (people[p.principal]?.name ?? <SubjectName id={p.principal} />) : p?.actor ? (agentName(p.actor) ?? p.actor) : 'someone');
  const change = async (e: RiskEntry, patch: RiskPatch) => {
    setError(undefined);
    try {
      await routes.changeRisk(projectId, e.uid, e.version, patch);
      data.reload();
    } catch (err) {
      const body = (err as { body?: { message?: string; error?: string } }).body;
      setError(body?.error === 'precondition_failed' ? 'Someone else changed it first; the list has been refreshed.' : (body?.message ?? (err as Error).message));
      data.reload();
    }
  };
  return (
    <>
      {data.data ? (
        <RiskOverview entries={all} settings={settings.data} now={now} tone={tone as 'blue'} {...(focus ? { focus } : {})} onFocus={setFocus} />
      ) : (
        <div className="art-stats">
          <Stat tone={tone} icon={<TriangleExclamation />} label="Open risks" value={undefined} detail="Reading the register" />
          <Stat tone={tone} icon={<TriangleExclamation />} label="Open issues" value={undefined} detail="Reading the register" />
        </div>
      )}
      <ListGroup
        title="Risks and issues"
        count={data.data ? entries.length : undefined}
        description={
          focus ? (
            <button type="button" className="risk-filter-chip" onClick={() => setFocus(undefined)} aria-label={`Show every entry again, not only ${focus.label}`} data-risk-focus>
              Only {focus.label} ×
            </button>
          ) : (
            'A risk may happen; an issue has; a bug is a fault in what was built. Most pressing first.'
          )
        }
        actions={
          <>
            {filter.control}
            <Segment aria-label="Which entries" size="sm" selectedKey={focus ? '' : state} onSelectionChange={(k) => (setFocus(undefined), setState(String(k) as typeof state))} className="w-fit">
              <Segment.Item id="open">Open</Segment.Item>
              <Segment.Item id="closed">Settled</Segment.Item>
              <Segment.Item id="all">All</Segment.Item>
            </Segment>
            {canWrite && (
              <ChoiceSelect label="Raise" value={undefined} placeholder="Raise…" options={{ risk: 'A risk', issue: 'An issue', bug: 'A bug' } as Record<RiskKind, string>} onChange={setRaising} />
            )}
          </>
        }
        empty={data.data && entries.length === 0 && !data.error && !error ? <Empty tone={tone} icon={<TriangleExclamation />} compact title={state === 'open' ? 'Nothing open' : 'Nothing here'} body={canWrite ? 'Raise a risk for what may go wrong, or an issue for what has. Agents can raise them too, from a workbench.' : 'Risks and issues raised on this project show here.'} /> : undefined}
      >
        {data.error || error ? (
          <ListBlock>
            <ErrorNote error={data.error ?? error} />
          </ListBlock>
        ) : null}
        {!data.data && !data.error ? <ListBlock className="text-sm text-muted">Reading the register…</ListBlock> : null}
        {entries.map((e) => {
          const owner = e.owner ? (e.ownerPerson?.name ?? people[e.owner]?.name ?? e.owner) : undefined;
          const meta = [
            e.id,
            e.rootId ? e.rootId : 'The project',
            canWrite ? undefined : RISK_STATUS_TEXT[e.status],
            e.kind === 'risk' ? `Likelihood ${LEVEL_TEXT[e.likelihood ?? 'medium'].toLowerCase()}` : undefined,
            e.kind === 'bug' && e.severity ? `Severity ${SEVERITY_TEXT[e.severity as BugSeverity].toLowerCase()}` : undefined,
            `Impact ${LEVEL_TEXT[e.impact].toLowerCase()}`,
            e.kind === 'bug' && e.foundIn ? `Found in ${e.foundIn}` : undefined,
            owner ? `Owner ${owner}` : undefined,
            e.due ? `Due ${dateShort(e.due)}` : undefined,
            e.roadmap.length > 0 || e.tasks.length > 0 ? `Threatens ${[...e.roadmap.map((r) => r.id), ...e.tasks.map((t) => t.uid)].join(', ')}` : undefined,
            manyTeams && e.raisedByTeam ? (e.raisedByTeam.name ?? e.raisedByTeam.id) : undefined,
            e.fromRisk ? 'From a risk that happened' : undefined,
          ].filter(Boolean);
          return (
            <ListRow
              key={e.uid}
              title={e.title}
              description={
                <>
                  {meta.join(' · ')}
                  <span className="block">
                    Raised by {who(e.raisedBy)} {timeAgo(e.raisedAt)}
                    {e.updatedAt !== e.raisedAt ? `, changed ${timeAgo(e.updatedAt)}` : ''}
                  </span>
                </>
              }
              status={<StatusMark state={RATING_STATE[riskRating(e)]} label={RISK_RATING_TEXT[riskRating(e)]} />}
              control={
                canWrite ? (
                  <>
                    <ChoiceSelect label={`Status of ${e.id}`} className="w-fit min-w-32 max-w-full" value={e.status} options={Object.fromEntries(statusesFor(e.kind).map((s) => [s, RISK_STATUS_TEXT[s]])) as Record<RiskStatus, string>} onChange={(s) => void change(e, { status: s })} />
                    <RowMenu
                      label={`More for ${e.id}`}
                      items={[
                        { id: 'owner', label: owner ? 'Change the owner…' : 'Set an owner…' },
                        isOpenEntry(e) && e.kind === 'risk' && e.status !== 'occurred' && { id: 'happened', label: 'It happened: raise an issue' },
                        { id: 'remove', label: 'Remove…', danger: true },
                      ]}
                      onAction={(id) => (id === 'owner' ? setChoosing(e.uid) : id === 'happened' ? void change(e, { status: 'occurred' }).then(() => setRaising('issue')) : setRemoving(e))}
                    />
                  </>
                ) : undefined
              }
            >
              {e.description || e.response || (canWrite && choosing === e.uid) ? (
                <div className="grid gap-1.5">
                  {e.description && <Markdown compact className="text-muted" text={e.description} />}
                  {e.response && (
                    <p className="m-0 text-sm">
                      <span className="text-muted">{e.kind === 'risk' ? 'Mitigation: ' : e.kind === 'bug' ? 'Fix: ' : 'Fix or workaround: '}</span>
                      {e.response}
                    </p>
                  )}
                  {canWrite && choosing === e.uid ? (
                    <OwnerPicker
                      projectId={projectId}
                      label={`Owner of ${e.id}`}
                      className="w-fit min-w-40 max-w-full"
                      value={e.owner}
                      onChange={(o) => {
                        setChoosing(undefined);
                        void change(e, { owner: o ? (o.id ?? o.name) : null });
                      }}
                    />
                  ) : null}
                </div>
              ) : null}
            </ListRow>
          );
        })}
      </ListGroup>
      <ConfirmDialog
        isOpen={!!removing}
        title={`Remove ${removing?.id ?? 'the entry'}`}
        confirmLabel="Remove"
        danger
        onCancel={() => setRemoving(undefined)}
        onConfirm={async () => {
          const r = removing;
          setRemoving(undefined);
          if (r) await routes.removeRisk(projectId, r.uid).then(data.reload);
        }}
      >
        The record of it stays in the activity log.
      </ConfirmDialog>
      {raising && <RaiseForm projectId={projectId} kind={raising} roots={roots} onDone={() => (setRaising(undefined), data.reload())} onCancel={() => setRaising(undefined)} />}
    </>
  );
}

function RaiseForm({ projectId, kind, roots, onDone, onCancel }: { projectId: string; kind: RiskKind; roots: string[]; onDone: () => void; onCancel: () => void }) {
  const [d, setD] = useState<{ title: string; description: string; rootId: string; likelihood: RiskLevel; impact: RiskLevel; response: string; due: string; owner: string; severity: BugSeverity; repro: string; foundIn: string }>({ title: '', description: '', rootId: '', likelihood: 'medium', impact: 'medium', response: '', due: '', owner: '', severity: 'major', repro: '', foundIn: '' });
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const set = (patch: Partial<typeof d>) => setD((x) => ({ ...x, ...patch }));
  const save = async () => {
    setSaving(true);
    setError(undefined);
    const draft: RiskDraft = { kind, rootId: d.rootId, title: d.title.trim(), status: 'open', impact: d.impact, roadmap: [], tasks: [], ...(kind === 'risk' ? { likelihood: d.likelihood } : {}), ...(d.description.trim() ? { description: d.description.trim() } : {}), ...(d.response.trim() ? { response: d.response.trim() } : {}), ...(d.due ? { due: d.due } : {}), ...(d.owner ? { owner: d.owner } : {}), ...(kind === 'bug' ? { severity: d.severity, ...(d.repro.trim() ? { repro: d.repro.trim() } : {}), ...(d.foundIn.trim() ? { foundIn: d.foundIn.trim() } : {}) } : {}) };
    try {
      await routes.raiseRisk(projectId, draft);
      onDone();
    } catch (e) {
      setError(((e as { body?: { message?: string } }).body?.message) ?? (e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <FormSheet
      title={kind === 'risk' ? 'Raise a risk' : kind === 'bug' ? 'Raise a bug' : 'Raise an issue'}
      description={`${kind === 'risk' ? 'Something that may happen.' : kind === 'bug' ? 'A fault in what was built.' : 'Something that has happened.'} It lands in the project's register and its activity log, signed by you. Rated ${RISK_RATING_TEXT[riskRating({ kind, impact: d.impact, likelihood: d.likelihood, ...(kind === 'bug' ? { severity: d.severity } : {}) })].toLowerCase()} as it stands.`}
      onClose={onCancel}
      footer={
        <>
          <Button size="sm" variant="ghost" onPress={onCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isPending={saving} isDisabled={!d.title.trim()} onPress={() => void save()}>
            Raise
          </Button>
        </>
      }
    >
      <FormField label="Title">
        <input className={inputClass} value={d.title} onChange={(e) => set({ title: e.target.value })} placeholder={kind === 'risk' ? 'The vendor API changes under us' : kind === 'bug' ? 'Sign-in loops on Safari' : 'The build broke on main'} />
      </FormField>
      <FormField label="Description">
        <textarea className={`${inputClass} min-h-16`} value={d.description} onChange={(e) => set({ description: e.target.value })} placeholder="What it is, in a sentence or two" />
      </FormField>
      <div className="@container">
      <div className="grid gap-4 @[24rem]:grid-cols-2">
        <FormField label="Belongs to">
          <ChoiceSelect label="Belongs to" className="w-full" value={d.rootId || NONE} options={{ [NONE]: 'The whole project', ...Object.fromEntries(roots.map((r) => [r, r])) }} onChange={(v) => set({ rootId: v === NONE ? '' : v })} />
        </FormField>
        {kind === 'risk' && (
          <FormField label="Likelihood">
            <ChoiceSelect label="Likelihood" className="w-full" value={d.likelihood} options={Object.fromEntries(RISK_LEVELS.map((l) => [l, LEVEL_TEXT[l]])) as Record<RiskLevel, string>} onChange={(v) => set({ likelihood: v })} />
          </FormField>
        )}
        {kind === 'bug' && (
          <FormField label="Severity">
            <ChoiceSelect label="Severity" className="w-full" value={d.severity} options={Object.fromEntries(BUG_SEVERITIES.map((v) => [v, SEVERITY_TEXT[v]])) as Record<BugSeverity, string>} onChange={(v) => set({ severity: v })} />
          </FormField>
        )}
        <FormField label="Impact">
          <ChoiceSelect label="Impact" className="w-full" value={d.impact} options={Object.fromEntries(RISK_LEVELS.map((l) => [l, LEVEL_TEXT[l]])) as Record<RiskLevel, string>} onChange={(v) => set({ impact: v })} />
        </FormField>
        <FormField label="Deal with it by">
          <input type="date" className={inputClass} value={d.due} onChange={(e) => set({ due: e.target.value })} />
        </FormField>
      </div>
      </div>
      {kind === 'bug' && (
        <>
          <FormField label="How to reproduce it">
            <textarea className={`${inputClass} min-h-16`} value={d.repro} onChange={(e) => set({ repro: e.target.value })} placeholder="How to make it happen, step by step" />
          </FormField>
          <FormField label="Found in">
            <input className={inputClass} value={d.foundIn} onChange={(e) => set({ foundIn: e.target.value })} placeholder="Found in version" />
          </FormField>
        </>
      )}
      <div className="grid min-w-0 content-start gap-1.5">
        <span className="text-xs font-medium text-foreground">Who looks after it</span>
        <OwnerPicker projectId={projectId} label="Owner" value={d.owner || undefined} onChange={(o) => set({ owner: o ? (o.id ?? o.name) : '' })} />
      </div>
      <FormField label={kind === 'risk' ? 'Mitigation' : 'Fix or workaround'}>
        <input className={inputClass} value={d.response} onChange={(e) => set({ response: e.target.value })} placeholder={kind === 'risk' ? 'What is being done about it' : 'The fix or the workaround'} />
      </FormField>
      <ErrorNote error={error} />
    </FormSheet>
  );
}
